import {
  type AxiosRequestConfig,
  publicHttpClient,
} from '@ahm/api-wrappers-core';
import { apiRoutes } from '@/api/api-routes';

/**
 * CampaignBanner — hệ banner mới (AF-062), gộp Strapi hero + /tag super-deal
 * vào một API public v4. Webstore: FEATURED_BANNER → hero, SUPER_DEAL → dải deal.
 */
export type BannerScreen =
  | 'home'
  | 'city_landing'
  | 'store_listing'
  | 'store_detail';

export type BannerType =
  | 'FEATURED_BANNER'
  | 'HOT_DEAL'
  | 'SUPER_DEAL'
  | 'ADS'
  | 'STORE_COLLECTION';

export type ServiceType = 'AIFOOD' | 'RIDE' | 'EXPRESS';

/** Nguồn bộ ảnh chiến dịch (AF-063): AI tạo hay partner tự tải lên. */
export interface CampaignImageMeta {
  source: 'ai' | 'upload';
  /** Mã bộ ảnh — chung cho 2 banner của cùng một chiến dịch partner. */
  set_id?: string;
  /** `null` = AI tự chọn kiểu, `""` = không chọn. */
  style?: string | null;
  style_auto?: boolean;
  frame?: string | null;
  frame_auto?: boolean;
  /** Tên chiến dịch đã in sẵn lên ảnh. */
  text_name?: string;
}

/**
 * Một chiến dịch partner (AF-063, BE đổi 12/10) = HAI banner, mỗi banner một
 * ảnh trong `image_url`:
 *  - FEATURED_BANNER — banner ngang 21:9 (2100×900): hero trang chủ + đầu trang
 *    chiến dịch.
 *  - SUPER_DEAL — ảnh vuông 1:1 (1080²) + store_ids: thẻ dải Super Deal.
 * BE không nối hai banner với nhau. Khung ảnh món không còn nằm trên banner mà
 * nằm trên chính món (`frame_image_url` của item).
 */
export interface CampaignBanner {
  _id: number;
  type: BannerType;
  /** Ảnh chính — hiển thị mọi breakpoint (FE crop/focal-point cho mobile). */
  image_url: string;
  image_meta?: CampaignImageMeta;
  title_vi_vn: string;
  title_en_us?: string;
  description_vi_vn?: string;
  description_en_us?: string;
  background_image_url?: string;
  /** Link mở web — CTA khi bấm banner. */
  link?: string;
  deep_link?: string;
  screen?: BannerScreen[];
  service_types?: ServiceType[];
  city_ids?: string[];
  store_ids?: number[];
  order?: number;
  color?: string;
  start_time?: number;
  end_time?: number;
}

/** URL ảnh đầu tiên có giá trị — bỏ qua cả null/undefined lẫn chuỗi rỗng. */
export const pickImageUrl = (
  ...urls: (string | null | undefined)[]
): string | undefined => urls.find((u) => u?.trim()) ?? undefined;

/**
 * Banner cùng chiến dịch với `banner` nhưng khác loại — ghép theo
 * `image_meta.set_id`, khoá chung duy nhất của 2 banner trong response public
 * (không có store_ids/link). Không có set_id (chiến dịch cũ, ảnh tự tải lên
 * không gắn mã) thì không ghép được → `undefined`, caller tự fallback.
 */
export const findPairedBanner = (
  banner: CampaignBanner,
  banners: CampaignBanner[],
  type: BannerType
): CampaignBanner | undefined => {
  const setId = banner.image_meta?.set_id;
  if (!setId) return undefined;
  return banners.find(
    (b) =>
      b._id !== banner._id && b.type === type && b.image_meta?.set_id === setId
  );
};

export interface GetCampaignBannersParams {
  city_id: string;
  // BE hỗ trợ array (lặp key: type=A&type=B) → 1 call lấy nhiều type/screen.
  service_type?: ServiceType | ServiceType[];
  screen?: BannerScreen | BannerScreen[];
  type?: BannerType | BannerType[];
}

/**
 * Public — webstore đọc banner (lọc + sort do BE). Response có thể là mảng trần
 * hoặc `{ banners: [...] }` → chuẩn hoá về mảng.
 */
export const getCampaignBannersFn = async (
  params: GetCampaignBannersParams,
  config: AxiosRequestConfig = {}
): Promise<CampaignBanner[]> => {
  const res = await publicHttpClient.get<
    CampaignBanner[] | { banners: CampaignBanner[] }
  >(apiRoutes.ahafood.store.get_campaign_banners, {
    params: { service_type: 'AIFOOD', ...params },
    // array → lặp key không index (type=A&type=B) đúng format BE mong đợi
    paramsSerializer: { indexes: null },
    ...config,
  });
  const data = res?.data;
  return Array.isArray(data) ? data : (data?.banners ?? []);
};
